import { Landing, LandingTheme, TemplateNames } from '@anm/api/modules/hosting';
import ColorPickerField from '@anm/components/ColorPicker/ColorPickerField';
import { ListItem } from '@anm/components/list/SelectionList';
import LayoutSelection from '@anm/components/video-landings/selection/LayoutSelection';
import LogoSelection from '@anm/components/video-landings/selection/LogoSelection';
import ThemeSelection from '@anm/components/video-landings/selection/ThemeSelection';
import { DEFAULT_WAVE_COLOR } from '@anm/constants/colors';
import { useCallback, FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import vtConfigs from '../../../../../../config';
import { brandActions } from '../../../../../../store/modules/brands';
import { landingsActions } from '../../../../../../store/modules/landings';
import { ControlGroupSubtitle } from '../../../../CustomizationForm/ControlGroup/Wrapper';
import usePaidThemeAndLayout from '../../../hooks/usePaidThemeAndLayout';
import WaveFooterCheckbox from '../WaveFooterCheckbox';
import * as semrush from '@anm/helpers/semrush';
import { SectionWithSubtitle } from '../Wrapper';
import { getSelectedItemIndex } from './helpers';
import { useBrandList, useBrandLogo } from './hooks';
import { hostedVideoSelectors } from '../../../../../../store/modules/hostedVideo';
import { getWebinarColorsByTheme } from '@anm/styles/landingThemeStyles';

const { supportedBrandingImageExtensions } = vtConfigs();

type DesignSelectionProps = {
  landing: Landing;
  isWebinar?: boolean;
};

const DesignSelection: FC<DesignSelectionProps> = ({ landing, isWebinar }) => {
  const dispatch = useDispatch();
  const [brandLogosList, currentLogoIndex, isPending, onLogoChange, onListChange] = useBrandLogo();
  const [brandList, currentBrandId, onBrandChange] = useBrandList();
  const selectedThemeIndex = getSelectedItemIndex('theme', landing);
  const selectedLayoutIndex = getSelectedItemIndex('layout', landing);

  const onThemeChange = useCallback(
    (item: ListItem) => {
      dispatch(
        landingsActions.updateLanding({
          ...landing,
          parameters: {
            ...landing.parameters,
            theme: item.name as LandingTheme,
            ...(isWebinar && { playerColor: getWebinarColorsByTheme(item.name as LandingTheme).playerColor })
          }
        })
      );
    },
    [landing, isWebinar]
  );

  const onLayoutChange = useCallback(
    (item: ListItem) => {
      if (item.name === landing.template) return;

      dispatch(
        landingsActions.updateLanding({
          ...landing,
          template: item.name as TemplateNames
        })
      );
    },
    [landing]
  );

  const allowedImageExtensions = supportedBrandingImageExtensions.map(e => `.${e}`).join();

  const handleRemoveLogo = useCallback(
    (logoId: string) => {
      if (!currentBrandId) return;

      dispatch(
        brandActions.deleteBrandLogo({
          logoId,
          brandId: currentBrandId
        })
      );
    },
    [currentBrandId]
  );

  const handleColorChange = (color: string) =>
    dispatch(
      landingsActions.updateLanding({
        ...landing,
        parameters: {
          ...landing.parameters,
          playerColor: color
        }
      })
    );

  const manifestColor = useSelector(hostedVideoSelectors.selectPlayerColor) || DEFAULT_WAVE_COLOR;
  const playerColor = isWebinar ? landing.parameters?.playerColor || DEFAULT_WAVE_COLOR : manifestColor;
  const { paidThemes, paidLayouts } = usePaidThemeAndLayout();

  return (
    <>
      <LogoSelection
        list={brandLogosList}
        selectedLogoIndex={currentLogoIndex}
        isPendingUploadLogo={isPending}
        {...{
          brandList,
          onListChange,
          onBrandChange,
          currentBrandId,
          allowedImageExtensions
        }}
        onChange={onLogoChange}
        onRemoveLogo={handleRemoveLogo}
      />
      {!isWebinar && (
        <>
          <LayoutSelection {...{ paidLayouts, selectedLayoutIndex }} onChange={onLayoutChange} />
          {!semrush.isSemrushIframe() && <WaveFooterCheckbox />}
        </>
      )}
      <SectionWithSubtitle>
        <ThemeSelection
          {...{ paidThemes, playerColor, selectedThemeIndex }}
          isButtonIcon={!isWebinar}
          onChange={onThemeChange}
        >
          {isWebinar && (
            <>
              <SectionWithSubtitle>
                <ControlGroupSubtitle flow="vertical">Player color</ControlGroupSubtitle>
                <ColorPickerField initialColor={playerColor} onColorChange={handleColorChange} />
              </SectionWithSubtitle>
              <ControlGroupSubtitle flow="vertical">Webinar page theme</ControlGroupSubtitle>
            </>
          )}
        </ThemeSelection>
      </SectionWithSubtitle>
    </>
  );
};

export default DesignSelection;
